import React, { useState, useEffect, useCallback, useRef } from "react";
import { heroData } from "../data/mockData";
import { ArrowRight, CirclePlay, Play } from "lucide-react";
import { useBookDemo } from "../hooks/useBookDemo";

const DASHBOARD_IMAGE = "https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/42ty74qh_Frame%201000005856%20%281%29.png";

const Hero = () => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  // Start with the first word fully typed so SEO/prerender crawlers capture a
  // complete H1 sentence ("...platform for Educators") instead of a truncated
  // mid-typing snapshot. Animation cycles normally after mount.
  const [displayedLetters, setDisplayedLetters] = useState(
    heroData.rotatingWords[0].text.length
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const timeoutRef = useRef(null);
  const bookBtnRef = useRef(null);
  const seeBtnRef = useRef(null);
  const { openBookDemo } = useBookDemo();

  const currentWord = heroData.rotatingWords[currentWordIndex];
  const fullText = currentWord.text;

  // Lock button widths on mount so hover animations don't change button size
  useEffect(() => {
    if (bookBtnRef.current) {
      bookBtnRef.current.style.width = `${bookBtnRef.current.offsetWidth}px`;
    }
    if (seeBtnRef.current) {
      seeBtnRef.current.style.width = `${seeBtnRef.current.offsetWidth}px`;
    }
  }, []);

  useEffect(() => {
    if (!isDeleting && displayedLetters < fullText.length) {
      // Typing: add one letter at a time
      timeoutRef.current = setTimeout(() => {
        setDisplayedLetters((prev) => prev + 1);
      }, 70); // speed per letter
    } else if (!isDeleting && displayedLetters === fullText.length) {
      // Word fully typed — immediately start deleting (no delay)
      timeoutRef.current = setTimeout(() => {
        setIsDeleting(true);
      }, 1000); // Very short pause (1 second) before deleting
    } else if (isDeleting && displayedLetters > 0) {
      // Deleting: remove one letter at a time
      timeoutRef.current = setTimeout(() => {
        setDisplayedLetters((prev) => prev - 1);
      }, 40);
    } else if (isDeleting && displayedLetters === 0) {
      // Word fully deleted — immediately move to next word (no delay)
      setIsDeleting(false);
      setCurrentWordIndex((prev) => (prev + 1) % heroData.rotatingWords.length);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [displayedLetters, isDeleting, fullText.length]);

  const visibleText = fullText.slice(0, displayedLetters);

  return (
    <section className="pt-[72px]">
      <div className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Hero Text Content */}
        <div className="flex flex-col items-center text-center pt-14 md:pt-20 pb-10">
          {/* Main Heading — very bold/black weight */}
          <h1
            className="text-[#1a1a2e] max-w-[900px] tracking-[0.02em]"
            style={{
              fontSize: "clamp(32px, 6.4vw, 75px)",
              fontWeight: 900,
              fontFamily: "'DM Sans', sans-serif",
              lineHeight: "1.15",
              wordSpacing: "0.05em"
            }}
          >
            The AI learning
            <br />
            platform for
            <br className="sm:hidden" />
            {/* Animated word with colored highlight - with increased spacing */}
            <span className="relative inline-block align-baseline sm:ml-6">
              {/* Colored background highlight — fits text exactly */}
              <span
                className="absolute inset-0 rounded-lg -skew-x-1"
                style={{
                  backgroundColor: currentWord.color,
                  opacity: displayedLetters > 0 ? 1 : 0,
                  marginLeft: "-8px",
                  marginRight: "-8px",
                  marginTop: "-4px",
                  marginBottom: "-4px",
                }}
                aria-hidden="true"
              />
              {/* Visible text */}
              <span
                className="relative z-10 text-white"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontStyle: "italic",
                  fontWeight: 700,
                }}
              >
                {visibleText}
              </span>
              {/* Invisible placeholder - reserves space for longest word to prevent shifting */}
              <span
                className="invisible absolute left-0"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontStyle: "italic",
                  fontWeight: 700,
                }}
                aria-hidden="true"
              >
                Enterprises
              </span>
            </span>
          </h1>

          {/* Subtitle — regular weight, darker gray */}
          <p
            className="mt-6 text-gray-600 font-normal max-w-[440px]"
            style={{
              fontSize: "clamp(16px, 2vw, 20px)",
            }}
          >
            {heroData.subtitle}
          </p>

          {/* Buttons + Tags — compact card aligned under subtitle */}
          <div className="mt-10 max-w-[440px]">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300 w-full">
              {/* CTA Buttons Row */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3">
                {/* Book a demo — split button with CSS hover expansion */}
                <a
                  ref={bookBtnRef}
                  href={heroData.ctaPrimary.href}
                  onClick={openBookDemo}
                  className="book-demo-btn inline-flex items-center justify-center w-full sm:w-auto rounded-xl border-[2.5px] border-[#1a1a2e] overflow-hidden relative h-[52px]"
                >
                  {/* Purple section */}
                  <span className="book-demo-purple relative z-10 flex flex-1 sm:flex-initial items-center justify-center gap-2 text-white text-[15px] font-bold bg-[#8B5CF6] h-full py-[14px] px-5">
                    <span className="book-demo-dot w-2.5 h-2.5 rounded-sm bg-[#10B981] flex-shrink-0" />
                    Book a demo
                  </span>
                  {/* Arrow section */}
                  <span className="book-demo-arrow flex items-center justify-center bg-white text-[#1a1a2e] border-l-[2.5px] border-[#1a1a2e] h-full py-[14px] px-4">
                    <ArrowRight className="w-5 h-5 stroke-[2.5] flex-shrink-0" />
                  </span>
                </a>

                {/* See in action — outlined with left-to-right black slide hover */}
                <a
                  ref={seeBtnRef}
                  href="#platform-section"
                  className="see-in-action-btn inline-flex items-center justify-center gap-2.5 border-[2.5px] border-[#1a1a2e] text-[#1a1a2e] text-[15px] font-bold rounded-xl h-[52px] px-6 whitespace-nowrap w-full sm:w-auto sm:flex-shrink-0"
                  data-testid="see-in-action-btn"
                >
                  <CirclePlay className="see-icon w-5 h-5 stroke-[2.5]" />
                  See in action
                </a>
              </div>

              {/* Tags strip — full-width gray bar at bottom */}
              <div className="flex items-center justify-center gap-3 py-2.5 bg-gray-100">
                {heroData.tags.map((tag, index) => (
                  <React.Fragment key={tag}>
                    <span className="text-[13px] text-gray-500 font-medium">
                      {tag}
                    </span>
                    {index < heroData.tags.length - 1 && (
                      <span className="text-gray-400 text-[8px]">●</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Screenshot Image - NO fading, NO overlay */}
        <div className="relative max-w-[960px] mx-auto">
          <div className="rounded-2xl overflow-hidden shadow-2xl shadow-gray-300/50 border border-gray-200 relative">
            <img
              src={DASHBOARD_IMAGE}
              alt="elevante.ai Dashboard"
              className="w-full h-auto block"
              loading="eager"
            />
          </div>
        </div>
      </div>

      {/* Trust / Logo Bar */}
      <div className="mt-14">
        <div className="border-t border-gray-200" />
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-10">
          <p className="text-center text-[14px] text-gray-400 font-normal mb-7" style={{ fontStyle: "italic" }}>
            10,000+ business professionals trained
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 md:gap-x-16 gap-y-6">
            {/* MyCaptain */}
            <div className="h-12 flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/jr09aty2_image.png" 
                alt="MyCaptain" 
                className="h-full w-auto object-contain"
                loading="lazy"
              />
            </div>
            
            {/* Konverge AI */}
            <div className="h-12 flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/hi0apl0p_image.png" 
                alt="Konverge AI" 
                className="h-full w-auto object-contain"
                loading="lazy"
              />
            </div>
            
            {/* Azim Premji University */}
            <div className="h-12 flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/9qpmve3h_image.png" 
                alt="Azim Premji University" 
                className="h-full w-auto object-contain"
                loading="lazy"
              />
            </div>
            
            {/* IMARTICUS */}
            <div className="h-12 flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/k95k33q7_azim.png" 
                alt="IMARTICUS" 
                className="h-full w-auto object-contain"
                loading="lazy"
              />
            </div>
            
            {/* Generally Critical */}
            <div className="h-12 flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/88bv4mla_Frame%202147258597.png" 
                alt="Generally Critical" 
                className="h-full w-auto object-contain"
                loading="lazy"
              />
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200" />
      </div>
    </section>
  );
};

export default Hero;
